/**
 * Simulador de escala — a mesma conta da planilha "Calculadora QLP x Freelancer"
 * (abas Calculadora, Parâmetros e Escala), em funções puras. Roda só no navegador:
 * nada é gravado no sistema.
 */

export type SimParams = {
  /** Dias em que a loja abre no mês. */
  daysInMonth: number;
  /** O horário da loja é dividido igualmente entre os turnos. */
  shiftsPerDay: number;
  /** Intervalo descontado de cada turno, em horas. */
  breakHours: number;
  /** 6 = 6x1; 5 = 5x2. */
  qlpWorkDaysPerWeek: number;
  /** Diárias de um turno que cada freelancer faz no mês. */
  freelancerShiftsPerMonth: number;
  /** Hora/homem abaixo deste valor: Demissão. */
  fireBelow: number;
  /** A partir deste valor: Contratar freelancer (entre o anterior e este: Manter). */
  freelancerFrom: number;
  /** A partir deste valor: Contratar QLP. */
  qlpFrom: number;
};

export const DEFAULT_PARAMS: SimParams = {
  daysInMonth: 30,
  shiftsPerDay: 2,
  breakHours: 1,
  qlpWorkDaysPerWeek: 6,
  freelancerShiftsPerMonth: 4,
  fireBelow: 10,
  freelancerFrom: 11,
  qlpFrom: 12,
};

export type StoreInput = {
  /** Volume mensal de pedidos. */
  volume: number | null;
  qlp: number | null;
  /** Opcional: em branco conta como zero. */
  freelancers: number | null;
  /** "HH:MM" */
  open: string;
  /** "HH:MM"; antes da abertura = fecha no dia seguinte. */
  close: string;
};

export const EMPTY_INPUT: StoreInput = { volume: null, qlp: null, freelancers: null, open: "", close: "" };

export type SimAction = "DEMISSAO" | "MANTER" | "CONTRATAR_FREELANCER" | "CONTRATAR_QLP";

export const ACTION_LABELS: Record<SimAction, string> = {
  DEMISSAO: "Demissão",
  MANTER: "Manter",
  CONTRATAR_FREELANCER: "Contratar freelancer",
  CONTRATAR_QLP: "Contratar QLP",
};

/** Mod como o MOD do Excel: o resultado tem o sinal do divisor. */
const mod = (a: number, b: number) => ((a % b) + b) % b;

const minutesOf = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

/** Horas abertas por dia: fechamento − abertura; fechamento antes da abertura = dia seguinte; iguais = 24 h. */
export function hoursOpen(open: string, close: string): number | null {
  const o = minutesOf(open);
  const c = minutesOf(close);
  if (o === null || c === null) return null;
  const diff = mod(c - o, 1440);
  return diff === 0 ? 24 : diff / 60;
}

/** Horas de trabalho de um turno = horas abertas ÷ turnos − intervalo (nunca negativo). */
export function shiftWorkHours(open: string, close: string, p: SimParams): number | null {
  const h = hoursOpen(open, close);
  if (h === null || p.shiftsPerDay <= 0) return null;
  return Math.max(0, h / p.shiftsPerDay - p.breakHours);
}

/**
 * Hora/homem = volume mensal ÷ horas trabalhadas no mês, com
 * horas no mês = (QLP × dias do mês × dias de trabalho por semana ÷ 7 + freelancers × diárias) × horas de um turno.
 * Sem volume, QLP ou horário: sem resultado (como a célula em branco da planilha).
 */
export function hourMan(input: StoreInput, p: SimParams): number | null {
  if (input.volume === null || input.qlp === null) return null;
  const shift = shiftWorkHours(input.open, input.close, p);
  if (shift === null) return null;
  const hours = (input.qlp * p.daysInMonth * p.qlpWorkDaysPerWeek / 7 + (input.freelancers ?? 0) * p.freelancerShiftsPerMonth) * shift;
  if (hours === 0) return null;
  return input.volume / hours;
}

/** Arredonda em uma casa, como o ROUND(x;1) do Excel para valores positivos. */
export const round1 = (x: number) => Math.round((x + Number.EPSILON) * 10) / 10;

/** Ação pelo valor arredondado em uma casa: o mesmo número que aparece na tela. */
export function actionOf(hh: number | null, p: SimParams): SimAction | null {
  if (hh === null) return null;
  const r = round1(hh);
  if (r < p.fireBelow) return "DEMISSAO";
  if (r < p.freelancerFrom) return "MANTER";
  if (r < p.qlpFrom) return "CONTRATAR_FREELANCER";
  return "CONTRATAR_QLP";
}

// ---------------------------------------------------------------------------
// Escala 5x2
// ---------------------------------------------------------------------------

export type SimShift = "MANHA" | "NOITE";
export type SimCell = "M" | "N" | "F" | "";

export type SimRosterRow = {
  label: string;
  shift: SimShift;
  cells: SimCell[];
  rests: number;
  workDays: number;
};

export type SimRoster = {
  days: Date[];
  rows: SimRosterRow[];
  morning: number[];
  night: number[];
  total: number[];
  qlpOff: number[];
};

/** Teto de linhas da grade, para a tela não travar com um número digitado errado. */
export const MAX_ROSTER_ROWS = 200;

const DAY_MS = 86_400_000;
/** 01/01/2024 é uma segunda: as semanas (segunda a domingo) contam daí e a rotação continua entre meses. */
const WEEK_ORIGIN = Date.UTC(2024, 0, 1);

export function monthDays(year: number, month: number): Date[] {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => new Date(Date.UTC(year, month - 1, i + 1)));
}

/** 0 = segunda … 6 = domingo. */
const weekdayMon0 = (d: Date) => (d.getUTCDay() + 6) % 7;

/**
 * Escala 5x2 da loja no mês, como a aba Escala da planilha, só com o QLP: metade (arredondada
 * para cima) na manhã, o resto na noite. Cada um folga 2 dias por semana (segunda a domingo): o 1º
 * no dia k e o 2º em k + 3, nunca encostados. Dentro do turno, k começa espalhado pela semana
 * (posição × 7 ÷ pessoas do turno) e anda 3 dias a cada semana.
 *
 * Os freelancers ficam fora da escala (decisão do usuário, 21/10/2026): contam só na hora/homem.
 */
export function buildRoster(year: number, month: number, qlpCount: number): SimRoster {
  const days = monthDays(year, month);
  const qlp = Math.max(0, Math.min(MAX_ROSTER_ROWS, Math.floor(qlpCount)));
  const rows: SimRosterRow[] = [];

  const nm = Math.ceil(qlp / 2);
  for (let i = 1; i <= qlp; i += 1) {
    const morning = i <= nm;
    const g = morning ? i : i - nm;
    const n = morning ? nm : qlp - nm;
    const letter: SimCell = morning ? "M" : "N";
    const cells = days.map((d): SimCell => {
      const week = Math.floor((d.getTime() - WEEK_ORIGIN) / DAY_MS / 7);
      const k = mod(Math.floor(((g - 1) * 7) / n) + 3 * week, 7);
      const off = weekdayMon0(d);
      return off === k || off === mod(k + 3, 7) ? "F" : letter;
    });
    rows.push({
      label: `QLP ${i}`,
      shift: morning ? "MANHA" : "NOITE",
      cells,
      rests: cells.filter((c) => c === "F").length,
      workDays: cells.filter((c) => c === "M" || c === "N").length,
    });
  }

  const column = (di: number, value: SimCell) => rows.filter((r) => r.cells[di] === value).length;
  const morning = days.map((_, di) => column(di, "M"));
  const night = days.map((_, di) => column(di, "N"));
  return {
    days,
    rows,
    morning,
    night,
    total: days.map((_, di) => morning[di] + night[di]),
    qlpOff: days.map((_, di) => column(di, "F")),
  };
}
